/**
 * Plan Comparison Table
 *
 * Responsive comparison of Free vs Pro vs Enterprise plans.
 * Fetches live plan data from the subscription API and renders a feature matrix.
 *
 * - Desktop: side-by-side table
 * - Mobile: stacked cards per plan
 * - Highlights the "Most Popular" column
 * - Uses featureFlags and limits from plan data (not hardcoded)
 */

import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Check, X as XIcon, Infinity, Crown, Sparkles, Building, AlertTriangle } from 'lucide-react';
import { BrandedLoading } from '@/components/common/BrandedLoading';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/Button';
import { useNavigate } from 'react-router-dom';
import { subscriptionPlanApi } from '@/services/subscriptionApi';
import type { SubscriptionPlan } from '@/types/subscription';

// ---------------------------------------------------------------------------
// Feature definition: label + how to extract value from a plan
// ---------------------------------------------------------------------------

interface FeatureRow {
  label: string;
  /** Extract a display value from the plan. Returns boolean, number, or string. */
  getValue: (plan: SubscriptionPlan) => boolean | number | string | null | undefined;
  /** If true, treat numbers as limits (-1 = Unlimited). */
  isLimit?: boolean;
}

const FEATURE_ROWS: FeatureRow[] = [
  {
    label: 'AI Coach sessions',
    getValue: (p) => p.maxCoachSessionsPerDay ?? p.limits?.dailyCoachSessions ?? null,
    isLimit: true,
  },
  {
    label: 'Learning paths',
    getValue: (p) => p.maxLearningPaths ?? p.limits?.learningPaths ?? null,
    isLimit: true,
  },
  {
    label: 'Daily token limit',
    getValue: (p) => p.dailyTokenLimit ?? p.limits?.dailyTokens ?? null,
    isLimit: true,
  },
  {
    label: 'Goal tracking',
    getValue: (p) => p.featureFlags?.multipleGoals ?? Boolean(p.features?.goalTracking) ?? false,
  },
  {
    label: 'Advanced analytics',
    getValue: (p) => p.featureFlags?.advancedAnalytics ?? false,
  },
  {
    label: 'Priority support',
    getValue: (p) => p.featureFlags?.prioritySupport ?? false,
  },
  {
    label: 'Data export',
    getValue: (p) => p.featureFlags?.exportData ?? false,
  },
  {
    label: 'Team management',
    getValue: (p) => p.featureFlags?.teamManagement ?? false,
  },
  {
    label: 'SSO',
    getValue: (p) => p.featureFlags?.sso ?? false,
  },
  {
    label: 'Custom branding',
    getValue: (p) => p.featureFlags?.customBranding ?? false,
  },
  {
    label: 'API access',
    getValue: (p) => p.featureFlags?.apiAccess ?? false,
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const ICON_MAP: Record<string, React.ElementType> = {
  sparkles: Sparkles,
  rocket: Crown,
  building: Building,
};

const formatLimitCell = (value: number | boolean | string | null | undefined, isLimit?: boolean): React.ReactNode => {
  if (value === true) return <Check className="h-4 w-4 text-emerald-600" />;
  if (value === false) return <XIcon className="h-4 w-4 text-muted-foreground/40" />;
  if (value === null || value === undefined) return <XIcon className="h-4 w-4 text-muted-foreground/40" />;
  if (isLimit && (value === -1 || value === '-1')) {
    return (
      <span className="inline-flex items-center gap-1 text-sm font-medium">
        <Infinity className="h-3.5 w-3.5" />
        Unlimited
      </span>
    );
  }
  if (typeof value === 'number') {
    return <span className="text-sm font-medium">{value.toLocaleString()}{isLimit ? '/day' : ''}</span>;
  }
  return <span className="text-sm font-medium">{String(value)}</span>;
};

const formatPrice = (plan: SubscriptionPlan): string => {
  if (plan.monthlyPrice === 0) return '$0';
  return `$${plan.monthlyPrice}/mo`;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface PlanComparisonTableProps {
  /** Optional: if provided, skip API fetch and use these plans directly. */
  plans?: SubscriptionPlan[];
  className?: string;
}

export const PlanComparisonTable: React.FC<PlanComparisonTableProps> = ({ plans: propPlans, className }) => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState<SubscriptionPlan[]>(propPlans ?? []);
  const [isLoading, setIsLoading] = useState(!propPlans);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (propPlans) {
      setPlans(propPlans);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        setIsLoading(true);
        const response = await subscriptionPlanApi.getActive({ size: 50, sort: 'displayOrder,asc' });
        if (cancelled) return;
        const sorted = (response.content ?? [])
          .filter((p) => p.status === 'ACTIVE' && p.isVisible !== false)
          .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
        setPlans(sorted);
      } catch (err) {
        if (!cancelled) setError((err as Error)?.message ?? 'Failed to load plans');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [propPlans]);

  if (isLoading) {
    return (
      <div className={`flex justify-center py-12 ${className ?? ''}`}>
        <BrandedLoading message="Loading subscription plans…" iconSize="sm" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground ${className ?? ''}`}>
        <AlertTriangle className="h-4 w-4" />
        {error}
      </div>
    );
  }

  if (plans.length === 0) return null;

  // --- Desktop table ---
  const desktopTable = (
    <div className="hidden md:block overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr>
            <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground w-48">Feature</th>
            {plans.map((plan) => {
              const Icon = ICON_MAP[plan.iconName ?? ''] ?? Sparkles;
              return (
                <th
                  key={plan.id}
                  className={`text-center py-3 px-4 ${
                    plan.isPopular
                      ? 'bg-gradient-to-b from-purple-500/10 to-transparent border-t-2 border-purple-500 rounded-t-xl'
                      : ''
                  }`}
                >
                  <div className="flex flex-col items-center gap-1">
                    {plan.isPopular && (
                      <Badge className="bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white border-0 text-[10px] mb-1">
                        {plan.badgeText ?? 'Most Popular'}
                      </Badge>
                    )}
                    <div className="flex items-center gap-1.5">
                      <Icon className="h-4 w-4 text-primary" />
                      <span className="font-semibold text-foreground">{plan.displayName}</span>
                    </div>
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {FEATURE_ROWS.map((row, idx) => (
            <tr key={row.label} className={idx % 2 === 0 ? 'bg-muted/30' : ''}>
              <td className="py-2.5 px-4 text-sm text-foreground">{row.label}</td>
              {plans.map((plan) => (
                <td
                  key={plan.id}
                  className={`py-2.5 px-4 text-center ${plan.isPopular ? 'bg-purple-500/5' : ''}`}
                >
                  {formatLimitCell(row.getValue(plan), row.isLimit)}
                </td>
              ))}
            </tr>
          ))}
          {/* Price row */}
          <tr className="border-t border-border">
            <td className="py-3 px-4 text-sm font-medium text-foreground">Price</td>
            {plans.map((plan) => (
              <td
                key={plan.id}
                className={`py-3 px-4 text-center ${plan.isPopular ? 'bg-purple-500/5' : ''}`}
              >
                <span className="text-lg font-bold text-foreground">{formatPrice(plan)}</span>
              </td>
            ))}
          </tr>
          {/* CTA row */}
          <tr>
            <td className="py-4 px-4" />
            {plans.map((plan) => (
              <td
                key={plan.id}
                className={`py-4 px-4 text-center ${plan.isPopular ? 'bg-purple-500/5 rounded-b-xl' : ''}`}
              >
                <Button
                  size="sm"
                  variant={plan.isPopular ? 'default' : 'outline'}
                  className={plan.isPopular ? 'bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white hover:from-purple-700 hover:to-fuchsia-700' : ''}
                  onClick={() => navigate('/billing/plans')}
                >
                  {plan.ctaText ?? 'Select Plan'}
                </Button>
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );

  // --- Mobile stacked cards ---
  const mobileCards = (
    <div className="md:hidden space-y-4">
      {plans.map((plan) => {
        const Icon = ICON_MAP[plan.iconName ?? ''] ?? Sparkles;
        return (
          <motion.div
            key={plan.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`rounded-xl border p-4 space-y-3 ${
              plan.isPopular
                ? 'border-purple-500/50 bg-gradient-to-b from-purple-500/10 to-transparent shadow-md shadow-purple-500/10'
                : 'border-border'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Icon className="h-5 w-5 text-primary" />
                <span className="font-semibold text-lg text-foreground">{plan.displayName}</span>
              </div>
              {plan.isPopular && (
                <Badge className="bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white border-0 text-[10px]">
                  {plan.badgeText ?? 'Most Popular'}
                </Badge>
              )}
            </div>
            <div className="text-2xl font-bold text-foreground">{formatPrice(plan)}</div>
            <div className="space-y-2">
              {FEATURE_ROWS.map((row) => {
                const value = row.getValue(plan);
                return (
                  <div key={row.label} className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{row.label}</span>
                    <span>{formatLimitCell(value, row.isLimit)}</span>
                  </div>
                );
              })}
            </div>
            <Button
              className={`w-full ${
                plan.isPopular
                  ? 'bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white hover:from-purple-700 hover:to-fuchsia-700'
                  : ''
              }`}
              variant={plan.isPopular ? 'default' : 'outline'}
              onClick={() => navigate('/billing/plans')}
            >
              {plan.ctaText ?? 'Select Plan'}
            </Button>
          </motion.div>
        );
      })}
    </div>
  );

  return (
    <div className={className}>
      {desktopTable}
      {mobileCards}
    </div>
  );
};
